import React from 'react';
import * as S from '../style';
import { AssetType, AssetDimensions } from '../types';

interface AssetUploadProps {
  type: AssetType;
  file: File | null;
  preview: string | null;
  dimensions: AssetDimensions | null;
  label: string;
  accept?: string;
  onFileUpload: (
    e: React.ChangeEvent<HTMLInputElement>,
    type: AssetType,
  ) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>, type: AssetType) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onRemove: () => void;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
}

export const AssetUpload: React.FC<AssetUploadProps> = ({
  type,
  file,
  preview,
  dimensions,
  label,
  accept,
  onFileUpload,
  onDrop,
  onDragOver,
  onRemove,
  required = false,
  disabled = false,
  multiple = false,
}) => {
  const inputId = `${type}Upload`;

  // Derive preview type from the actual file mime type, NOT from the accept string.
  // accept="image/*,video/*" on primary would incorrectly mark it as video otherwise.
  const isVideoFile = file?.type.startsWith('video/') ?? false;

  return (
    <S.UploadBox
      onDrop={disabled ? undefined : e => onDrop(e, type)}
      onDragOver={disabled ? undefined : onDragOver}
      style={disabled ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
    >
      {!file ? (
        <>
          <label
            htmlFor={disabled ? undefined : inputId}
            style={{
              cursor: disabled ? 'not-allowed' : 'pointer',
              textAlign: 'center',
            }}
          >
            <S.UploadPlus>+</S.UploadPlus>
            <S.PrimaryText>
              {label}
              {required && <span className="required">*</span>}
            </S.PrimaryText>
          </label>
          <S.HiddenInput
            id={inputId}
            type="file"
            accept={accept}
            disabled={disabled}
            multiple={multiple}
            onChange={e => onFileUpload(e, type)}
          />
        </>
      ) : (
        <S.FilePreview>
          <S.FileHeader>
            <S.PrimaryText>
              {label}
              {required && <span className="required">*</span>}
            </S.PrimaryText>
            <S.RemoveButton onClick={disabled ? undefined : onRemove}>
              ✕
            </S.RemoveButton>
          </S.FileHeader>

          {isVideoFile ? (
            <video
              src={preview || ''}
              controls
              style={{
                maxWidth: 250,
                maxHeight: 80,
                borderRadius: 4,
                backgroundColor: '#eaf5ec',
                padding: 4,
                objectFit: 'contain',
              }}
            />
          ) : (
            <S.ImagePreview src={preview || ''} alt="preview" />
          )}
          {dimensions && (
            <S.DimensionText>
              {dimensions.duration
                ? `${dimensions.duration}s`
                : `${dimensions.width}x${dimensions.height}`}
            </S.DimensionText>
          )}
        </S.FilePreview>
      )}
    </S.UploadBox>
  );
};
